// Job-title extraction from a listing URL alone. No network, no model.
// Path only, cleaned of routing furniture and IDs, matched against a fixed
// title vocabulary longest-first, with a short list of allowed qualifiers.

const DROP_SEGMENTS = new Set([
  'job',
  'jobs',
  'joblisting',
  'joblistings',
  'listing',
  'listings',
  'career',
  'careers',
  'vacancy',
  'vacancies',
  'position',
  'positions',
  'opening',
  'openings',
  'role',
  'roles',
  'company',
  'companies',
  'employer',
  'employers',
  'search',
  'apply',
  'view',
  'detail',
  'details',
  'en',
  'uk',
  'gb',
  'us',
  'eu',
])

// Multi-word titles (hyphenated). Matched before any single-word title.
const MULTI_TITLES = [
  'group-financial-controller',
  'deputy-financial-controller',
  'assistant-financial-controller',
  'financial-controller',
  'finance-controller',
  'financial-planning-analyst',
  'financial-planning-and-analysis',
  'fp-a-analyst',
  'fp-a-manager',
  'fp-a-director',
  'management-accountant',
  'financial-accountant',
  'group-accountant',
  'staff-accountant',
  'senior-accountant',
  'project-accountant',
  'cost-accountant',
  'fund-accountant',
  'revenue-accountant',
  'tax-accountant',
  'tax-manager',
  'tax-analyst',
  'tax-director',
  'treasury-analyst',
  'treasury-manager',
  'treasury-accountant',
  'finance-manager',
  'finance-director',
  'finance-business-partner',
  'commercial-finance-manager',
  'finance-analyst',
  'financial-analyst',
  'business-analyst',
  'business-intelligence-analyst',
  'systems-analyst',
  'reporting-analyst',
  'regulatory-reporting-analyst',
  'regulatory-reporting',
  'statutory-reporting-accountant',
  'group-reporting-manager',
  'consolidation-accountant',
  'accounts-payable-clerk',
  'accounts-receivable-clerk',
  'accounts-assistant',
  'payroll-manager',
  'payroll-administrator',
  'billing-analyst',
  'credit-controller',
  'credit-analyst',
  'credit-risk-analyst',
  'credit-manager',
  'risk-analyst',
  'risk-manager',
  'market-risk-analyst',
  'operational-risk-manager',
  'internal-auditor',
  'external-auditor',
  'audit-manager',
  'audit-senior',
  'audit-associate',
  'compliance-officer',
  'compliance-manager',
  'compliance-analyst',
  'financial-crime-analyst',
  'aml-analyst',
  'kyc-analyst',
  'investment-analyst',
  'investment-manager',
  'investment-associate',
  'equity-research-analyst',
  'research-analyst',
  'portfolio-manager',
  'portfolio-analyst',
  'fund-manager',
  'private-equity-associate',
  'venture-capital-associate',
  'corporate-finance-analyst',
  'corporate-development-manager',
  'm-a-analyst',
  'm-a-associate',
  'quantitative-analyst',
  'quantitative-researcher',
  'quantitative-developer',
  'fx-options-trader',
  'fx-trader',
  'rates-trader',
  'commodities-trader',
  'equity-trader',
  'derivatives-trader',
  'sales-trader',
  'proprietary-trader',
  'execution-trader',
  'trading-analyst',
  'middle-office-analyst',
  'back-office-analyst',
  'settlements-analyst',
  'operations-analyst',
  'operations-manager',
  'operations-director',
  'business-operations-manager',
  'supply-chain-manager',
  'procurement-manager',
  'logistics-manager',
  'category-manager',
  'actuarial-analyst',
  'pricing-analyst',
  'pricing-manager',
  'claims-handler',
  'claims-manager',
  'insurance-underwriter',
  'underwriting-manager',
  'data-analyst',
  'data-scientist',
  'data-engineer',
  'analytics-engineer',
  'machine-learning-engineer',
  'software-engineer',
  'senior-software-engineer',
  'backend-engineer',
  'frontend-engineer',
  'full-stack-engineer',
  'platform-engineer',
  'devops-engineer',
  'site-reliability-engineer',
  'security-engineer',
  'qa-engineer',
  'test-engineer',
  'mobile-engineer',
  'solutions-architect',
  'systems-engineer',
  'network-engineer',
  'cloud-engineer',
  'database-administrator',
  'it-support-analyst',
  'it-manager',
  'technical-lead',
  'engineering-manager',
  'product-manager',
  'senior-product-manager',
  'technical-product-manager',
  'product-owner',
  'product-designer',
  'ux-designer',
  'ui-designer',
  'ux-researcher',
  'graphic-designer',
  'brand-designer',
  'content-designer',
  'project-manager',
  'programme-manager',
  'program-manager',
  'delivery-manager',
  'scrum-master',
  'business-change-manager',
  'transformation-manager',
  'change-analyst',
  'process-improvement-manager',
  'management-consultant',
  'strategy-consultant',
  'implementation-consultant',
  'solutions-consultant',
  'technical-consultant',
  'marketing-manager',
  'marketing-executive',
  'digital-marketing-manager',
  'content-marketing-manager',
  'brand-manager',
  'growth-manager',
  'performance-marketing-manager',
  'communications-manager',
  'public-relations-manager',
  'social-media-manager',
  'account-executive',
  'account-manager',
  'key-account-manager',
  'sales-manager',
  'sales-executive',
  'sales-director',
  'business-development-manager',
  'business-development-representative',
  'customer-success-manager',
  'customer-service-advisor',
  'client-relationship-manager',
  'partnerships-manager',
  'hr-business-partner',
  'hr-manager',
  'hr-advisor',
  'people-partner',
  'talent-acquisition-partner',
  'talent-manager',
  'recruitment-consultant',
  'reward-analyst',
  'learning-and-development-manager',
  'general-counsel',
  'legal-counsel',
  'corporate-lawyer',
  'commercial-solicitor',
  'legal-assistant',
  'company-secretary',
  'office-manager',
  'executive-assistant',
  'personal-assistant',
  'facilities-manager',
  'property-manager',
  'quantity-surveyor',
  'site-manager',
  'civil-engineer',
  'mechanical-engineer',
  'electrical-engineer',
  'process-engineer',
  'manufacturing-engineer',
  'quality-manager',
  'health-and-safety-manager',
  'sustainability-manager',
  'esg-analyst',
  'chief-financial-officer',
  'chief-operating-officer',
  'chief-executive-officer',
  'chief-technology-officer',
  'chief-of-staff',
  'finance-graduate',
  'graduate-analyst',
  'trainee-accountant',
]

// Generic single-word titles. Only used when no multi-word title matched.
const SINGLE_TITLES = [
  'quantitative-analyst',
  'accountant',
  'controller',
  'bookkeeper',
  'auditor',
  'actuary',
  'underwriter',
  'trader',
  'broker',
  'economist',
  'quant',
  'paralegal',
  'solicitor',
  'lawyer',
  'barrister',
  'recruiter',
  'consultant',
  'analyst',
  'associate',
  'manager',
  'director',
  'engineer',
  'developer',
  'architect',
  'designer',
  'researcher',
  'scientist',
  'officer',
  'executive',
  'specialist',
  'coordinator',
  'administrator',
  'advisor',
  'assistant',
  'supervisor',
  'strategist',
  'technician',
  'planner',
  'buyer',
  'surveyor',
  'lead',
  'head',
  'partner',
  'clerk',
  'intern',
]

const QUALIFIERS = [
  'head-of',
  'director-of',
  'senior',
  'junior',
  'associate',
  'assistant',
  'lead',
  'principal',
  'chief',
  'deputy',
  'vp',
  'avp',
  'svp',
  'evp',
  'interim',
  'contract',
  'commercial',
  'group',
  'global',
  'regional',
]

const ABBREVIATIONS: Record<string, string> = {
  vp: 'VP',
  avp: 'AVP',
  svp: 'SVP',
  evp: 'EVP',
  fx: 'FX',
  hr: 'HR',
  ux: 'UX',
  ui: 'UI',
  it: 'IT',
  qa: 'QA',
  esg: 'ESG',
  aml: 'AML',
  kyc: 'KYC',
  ceo: 'CEO',
  cfo: 'CFO',
  coo: 'COO',
  cto: 'CTO',
}

const byLengthDesc = (a: string, b: string) => b.length - a.length
const MULTI_SORTED = [...MULTI_TITLES].sort(byLengthDesc)
const SINGLE_SORTED = [...SINGLE_TITLES].sort(byLengthDesc)

const isHexToken = (t: string) => /^[0-9a-f]{4,}$/.test(t) && /\d/.test(t)

function stripIds(segment: string) {
  let s = segment
  // UUID (8-4-4-4-12)
  s = s.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '')
  // "job107821226" style tokens
  s = s.replace(/\bjob\d{3,}\b/g, '')
  // trailing "-12345" (5+ digits), possibly repeated
  while (/-\d{5,}$/.test(s)) s = s.replace(/-\d{5,}$/, '')

  // runs of 3+ hex-looking tokens
  const tokens = s.split('-')
  const keep: string[] = []
  let run: string[] = []
  const flush = () => {
    if (run.length < 3) keep.push(...run)
    run = []
  }
  tokens.forEach((t) => {
    if (isHexToken(t)) run.push(t)
    else {
      flush()
      keep.push(t)
    }
  })
  flush()

  return keep.filter(Boolean).join('-')
}

function cleanSegments(url: string) {
  const path = url
    .split('?')[0]
    .split('#')[0]
    .replace(/^[a-z]+:\/\//i, '')
    .toLowerCase()
  const segments = path.split('/').slice(1)

  return segments
    .map((s) => stripIds(s.replace(/[_+]/g, '-').replace(/-{2,}/g, '-')))
    .filter((s) => s.length > 0)
    .filter((s) => !DROP_SEGMENTS.has(s))
    .filter((s) => !/^\d+$/.test(s))
    .filter((s) => !/^[a-z]{2}$/.test(s))
}

function domainOf(url: string) {
  return url
    .split('?')[0]
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0]
}

interface Hit {
  segment: string
  title: string
  index: number
}

// A match must sit on token boundaries, so "lead" never matches inside "leadership".
function findIn(segment: string, vocabulary: string[]): Hit | null {
  for (const entry of vocabulary) {
    let from = 0
    while (from <= segment.length) {
      const index = segment.indexOf(entry, from)
      if (index === -1) break
      const before = index === 0 ? '-' : segment[index - 1]
      const afterIndex = index + entry.length
      const after = afterIndex >= segment.length ? '-' : segment[afterIndex]
      if (before === '-' && after === '-') return { segment, title: entry, index }
      from = index + 1
    }
  }
  return null
}

function qualifiersBefore(segment: string, index: number) {
  const preceding = segment.slice(0, Math.max(0, index - 1))
  if (!preceding) return [] as string[]
  const tokens = preceding.split('-').filter(Boolean)

  const picked: string[] = []
  let cursor = tokens.length
  while (picked.length < 2 && cursor > 0) {
    const pair = `${tokens[cursor - 2]}-${tokens[cursor - 1]}`
    if (cursor >= 2 && QUALIFIERS.includes(pair)) {
      picked.unshift(pair)
      cursor -= 2
      continue
    }
    const single = tokens[cursor - 1]
    if (QUALIFIERS.includes(single)) {
      picked.unshift(single)
      cursor -= 1
      continue
    }
    break
  }
  return picked
}

function titleCase(slug: string) {
  const tokens = slug.split('-').filter(Boolean)
  const out: string[] = []

  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i]
    const next = tokens[i + 1]
    if ((t === 'fp' || t === 'm') && next === 'a') {
      out.push(t === 'fp' ? 'FP&A' : 'M&A')
      i += 1
      continue
    }
    if (ABBREVIATIONS[t]) {
      out.push(ABBREVIATIONS[t])
      continue
    }
    if (t === 'and' || t === 'of' || t === 'the') {
      out.push(t)
      continue
    }
    out.push(t.charAt(0).toUpperCase() + t.slice(1))
  }

  return out.join(' ')
}

export function deriveListingTitle(url: string) {
  const trimmed = url.trim()
  if (!trimmed) return 'Pasted listing'

  const segments = cleanSegments(trimmed)

  let hit: Hit | null = null
  for (const segment of segments) {
    hit = findIn(segment, MULTI_SORTED)
    if (hit) break
  }
  if (!hit) {
    for (const segment of segments) {
      hit = findIn(segment, SINGLE_SORTED)
      if (hit) break
    }
  }

  if (!hit) {
    const domain = domainOf(trimmed)
    return domain ? `${domain} listing` : 'Pasted listing'
  }

  const prefix = qualifiersBefore(hit.segment, hit.index)
  return titleCase([...prefix, hit.title].join('-'))
}
